/**
 * Диалог правки норматива урожайности — разделы 2 и 3 макета
 * `docs/superpowers/prototypes/season-planning-2027/09-yield-norms-screen.html`
 * (утверждён founder 28.08, AK-789).
 *
 * Один компонент на два уровня, но НЕ одна кнопка: «задаю общее по компании» и
 * «правлю конкретное хозяйство» — разные решения с разными последствиями, и
 * человек должен видеть, какое из них он принимает, ДО ввода цифры. Поэтому
 * уровень приходит пропом и не переключается внутри диалога.
 *
 * Что диалог обязан сказать:
 *   • от чего отталкиваемся — компанийная цифра рядом, чтобы было видно, что
 *     именно перебиваем;
 *   • что правка НЕ пересчитывает уже разложенные поля (граница ручки AK-762);
 *     без этой строки агроном ждёт, что план обновится сам;
 *   • как вернуться к компанийной — снятие своей цифры живёт тут же, а не в
 *     отдельном «где-то ещё».
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCropPicker } from '@/components/fields/card/api';
import {
  formatCHa,
  parseYieldInput,
  useDeleteYieldDefault,
  useUpsertYieldDefault,
  type YieldLevel,
  type YieldNormRow,
} from '@/lib/planning/yieldDefaults';
import { matchesQuery, pickableCrops } from '../plan-variant/AddCropDialog';
import { cn } from '@/lib/utils';

export function YieldNormDialog({
  level,
  farmId,
  farmName,
  seasonId,
  seasonYear,
  /** Строка, из которой открыли. `null` — заводим норматив с нуля (выбор культуры внутри). */
  row,
  rows,
  onClose,
}: {
  level: YieldLevel;
  farmId: string;
  farmName: string;
  seasonId: string | null;
  seasonYear: number;
  row: YieldNormRow | null;
  /** Все строки экрана — по ним диалог показывает уже заведённое по выбранной культуре. */
  rows: readonly YieldNormRow[];
  onClose: () => void;
}) {
  const crops = useCropPicker(row === null);
  const upsert = useUpsertYieldDefault();
  const remove = useDeleteYieldDefault();

  const [cropId, setCropId] = useState<string | null>(row?.cropId ?? null);
  const [cropQuery, setCropQuery] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** «На этот сезон» или «на любой» — оба уровня полноправны (AK-762). */
  const [scopeSeason, setScopeSeason] = useState<'season' | 'any'>(seasonId ? 'season' : 'any');

  const current = useMemo(
    () => rows.find((r) => r.cropId === cropId) ?? null,
    [rows, cropId],
  );
  const existing = level === 'farm' ? current?.farm ?? null : current?.company ?? null;

  // Цифру подставляем ту, что сейчас стоит НА ЭТОМ уровне: диалог правки
  // обязан открываться с тем, что человек правит, а не с пустым полем.
  useEffect(() => {
    setValue(existing ? String(existing.cHa) : '');
    setScopeSeason(existing ? (existing.seasonScoped ? 'season' : 'any') : seasonId ? 'season' : 'any');
    setError(null);
  }, [existing?.itemId, existing?.cHa, cropId, seasonId]);

  const cropOptions = useMemo(() => pickableCrops(crops.data ?? []), [crops.data]);
  const shownCrops = cropOptions.filter((c) => matchesQuery(c.name, cropQuery));
  const cropName = current?.cropName ?? cropOptions.find((c) => c.id === cropId)?.name ?? '';
  // Цифра, которую культура получает СЕЙЧАС, — серверный итог лестницы, а не
  // вывод по записям: диалог обязан назвать ровно то, что перебивают.
  const inheritedCHa =
    current?.status === 'from_company' ? current.effectiveCHa : null;
  // К чему вернётся хозяйство, если снять свою цифру, — запись уровня компании
  // («что заведено»), поэтому здесь именно она.
  const companyCHa = current?.company?.cHa ?? null;

  const busy = upsert.isPending || remove.isPending;

  const submit = () => {
    if (!cropId || busy) return;
    const parsed = parseYieldInput(value);
    if (parsed === undefined) {
      setError('Урожайность — число не меньше нуля, например 45 или 48,5');
      return;
    }
    upsert.mutate(
      {
        farm_id: farmId,
        crop_id: cropId,
        crop_name: cropName || 'культура',
        planned_yield_c_ha: parsed,
        scope: level,
        season_id: scopeSeason === 'season' ? seasonId : null,
      },
      { onSuccess: () => onClose() },
    );
  };

  const drop = () => {
    if (!existing || busy) return;
    remove.mutate(
      {
        defaultId: existing.itemId,
        farmId,
        cropName: cropName || 'культура',
        level,
        fallbackCHa: level === 'farm' ? companyCHa : null,
      },
      { onSuccess: () => onClose() },
    );
  };

  const title =
    level === 'company'
      ? 'Норматив по умолчанию для компании'
      : `Норматив урожайности для хозяйства «${farmName}»`;

  const description =
    level === 'company'
      ? 'Эту цифру унаследуют все хозяйства, где нет своей.'
      : inheritedCHa !== null && !existing
        ? `${cropName || 'Культура'} · сейчас хозяйство берёт цифру по умолчанию для компании — ${formatCHa(inheritedCHa)}.`
        : `${cropName || 'Культура'} · своя цифра хозяйства перебивает то, что задано по компании.`;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-4">
            {row === null && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="yield-norm-crop">Культура</Label>
                <Input
                  id="yield-norm-crop"
                  value={cropQuery}
                  onChange={(e) => setCropQuery(e.target.value)}
                  placeholder="Поиск по названию"
                  autoFocus
                />
                {crops.isLoading && <Skeleton className="h-24 w-full" />}
                {!crops.isLoading && cropOptions.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    В номенклатуре хозяйства нет ни одной культуры, связанной с общим
                    справочником. Заведите культуру в номенклатуру — тогда ей можно будет
                    задать норматив урожайности.
                  </p>
                )}
                {cropOptions.length > 0 && (
                  <ScrollArea className="max-h-48 rounded-md border border-border">
                    <div className="flex flex-col p-1">
                      {shownCrops.map((crop) => (
                        <button
                          key={crop.id}
                          type="button"
                          aria-pressed={crop.id === cropId}
                          onClick={() => setCropId(crop.id)}
                          className={cn(
                            'rounded-md px-3 py-2 text-left text-sm',
                            crop.id === cropId
                              ? 'bg-primary/10 font-medium text-primary'
                              : 'text-foreground hover:bg-accent',
                          )}
                        >
                          {crop.name}
                        </button>
                      ))}
                      {shownCrops.length === 0 && (
                        <p className="px-3 py-2 text-sm text-muted-foreground">
                          По запросу ничего не нашлось.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="yield-norm-value">Урожайность, ц/га</Label>
              <Input
                id="yield-norm-value"
                inputMode="decimal"
                value={value}
                autoFocus={row !== null}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(null);
                }}
                placeholder="45"
                aria-invalid={error ? true : undefined}
                className="max-w-[160px]"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            {seasonId && (
              <RadioGroup
                value={scopeSeason}
                onValueChange={(v) => setScopeSeason(v === 'any' ? 'any' : 'season')}
                aria-label="На какие сезоны действует норматив"
              >
                <label
                  htmlFor="yield-norm-season"
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                    scopeSeason === 'season' ? 'border-primary bg-primary/5' : 'border-border',
                  )}
                >
                  <RadioGroupItem value="season" id="yield-norm-season" className="mt-1" />
                  <span>
                    <Label htmlFor="yield-norm-season" className="cursor-pointer">
                      Только сезон {seasonYear}
                    </Label>
                    <span className="block text-xs text-muted-foreground">
                      Цифра для этого сезона; в других сезонах останется прежняя.
                    </span>
                  </span>
                </label>
                <label
                  htmlFor="yield-norm-any"
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                    scopeSeason === 'any' ? 'border-primary bg-primary/5' : 'border-border',
                  )}
                >
                  <RadioGroupItem value="any" id="yield-norm-any" className="mt-1" />
                  <span>
                    <Label htmlFor="yield-norm-any" className="cursor-pointer">
                      Любой сезон
                    </Label>
                    <span className="block text-xs text-muted-foreground">
                      Одна цифра на все сезоны — пока не заведёте отдельную под конкретный
                      год.
                    </span>
                  </span>
                </label>
              </RadioGroup>
            )}

            <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
              Это цифра для будущих полей. Уже разложенные поля с этой культурой не
              пересчитаются — чтобы подставить её им, вернитесь в посевной план и нажмите
              «Подтянуть нормативы урожайности».
            </p>
          </div>

          <DialogFooter className="mt-4 sm:justify-between">
            {existing ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={drop}>
                {level === 'farm' && companyCHa !== null
                  ? `Убрать переопределение — вернуться к ${formatCHa(companyCHa)} компании`
                  : 'Убрать норматив'}
              </Button>
            ) : (
              <span />
            )}
            <span className="flex gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
                Отмена
              </Button>
              <Button type="submit" disabled={busy || !cropId}>
                {upsert.isPending
                  ? 'Сохраняем…'
                  : level === 'company'
                    ? 'Сохранить норматив компании'
                    : 'Сохранить норматив'}
              </Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
